import { useState, useEffect } from "react";

export const OPENING_HOURS_SCHEDULE = [
  { day: "Monday", hours: "Closed", lastOrder: null, isClosed: true },
  { day: "Tuesday – Friday", hours: "16:30 – 23:00", lastOrder: "22:30", isClosed: false },
  { day: "Saturday – Sunday", hours: "13:00 – 23:00", lastOrder: "22:30", isClosed: false },
];

/**
 * Calculates current time details in Europe/London timezone (UK local time)
 */
export function getUKTimeDetails(date = new Date()) {
  const formatter = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/London",
    weekday: "long",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });

  const parts = formatter.formatToParts(date);
  let weekday = "Monday";
  let hourStr = "00";
  let minuteStr = "00";

  for (const part of parts) {
    if (part.type === "weekday") weekday = part.value;
    if (part.type === "hour") hourStr = part.value;
    if (part.type === "minute") minuteStr = part.value;
  }

  let hour = parseInt(hourStr, 10);
  if (hour === 24) hour = 0;
  const minute = parseInt(minuteStr, 10);
  const totalMinutes = hour * 60 + minute;
  const formattedTime = `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;

  return {
    weekday,
    hour,
    minute,
    totalMinutes,
    formattedTime,
  };
}

/**
 * Determines current restaurant open/closed status in Europe/London timezone
 */
export function getUKRestaurantStatus(date = new Date()) {
  const { weekday, totalMinutes, formattedTime } = getUKTimeDetails(date);

  // Monday is Closed all day
  if (weekday === "Monday") {
    return {
      isOpen: false,
      isLastOrder: false,
      statusLabel: "CLOSED TODAY",
      headerStatus: "CLOSED NOW",
      headerDetail: "OPENS TUE 16:30 UK",
      statusColor: "red",
      currentUKTime: formattedTime,
      weekday,
      nextOpening: "Opens Tuesday at 16:30",
    };
  }

  const isTueFri = ["Tuesday", "Wednesday", "Thursday", "Friday"].includes(weekday);
  const openMinutes = isTueFri ? 16 * 60 + 30 : 13 * 60; // 16:30 or 13:00
  const lastOrderMinutes = 22 * 60 + 30; // 22:30
  const closeMinutes = 23 * 60; // 23:00

  // Open window: between opening time and 23:00
  if (totalMinutes >= openMinutes && totalMinutes < closeMinutes) {
    const isLastOrder = totalMinutes >= lastOrderMinutes;
    return {
      isOpen: true,
      isLastOrder,
      statusLabel: isLastOrder ? "OPEN (LAST ORDER)" : "OPEN NOW",
      headerStatus: "OPEN NOW",
      headerDetail: "CLOSES 23:00 UK",
      statusColor: isLastOrder ? "amber" : "emerald",
      currentUKTime: formattedTime,
      weekday,
      nextOpening: isLastOrder
        ? "Kitchen closing soon • Closes at 23:00"
        : "Last order at 22:30 • Closes at 23:00",
    };
  }

  // Before opening today
  if (totalMinutes < openMinutes) {
    const openTimeStr = isTueFri ? "16:30" : "13:00";
    return {
      isOpen: false,
      isLastOrder: false,
      statusLabel: "CLOSED NOW",
      headerStatus: "CLOSED NOW",
      headerDetail: `OPENS ${openTimeStr} UK`,
      statusColor: "red",
      currentUKTime: formattedTime,
      weekday,
      nextOpening: `Opens today at ${openTimeStr}`,
    };
  }

  // After closing today (>= 23:00)
  let headerDetail = "OPENS 16:30 UK";
  let nextOpening = "Opens tomorrow";
  if (weekday === "Sunday") {
    headerDetail = "OPENS TUE 16:30 UK";
    nextOpening = "Opens Tuesday at 16:30";
  } else if (weekday === "Friday") {
    headerDetail = "OPENS SAT 13:00 UK";
    nextOpening = "Opens Saturday at 13:00";
  } else if (weekday === "Saturday") {
    headerDetail = "OPENS SUN 13:00 UK";
    nextOpening = "Opens Sunday at 13:00";
  } else if (isTueFri) {
    headerDetail = "OPENS 16:30 UK";
    nextOpening = "Opens tomorrow at 16:30";
  }

  return {
    isOpen: false,
    isLastOrder: false,
    statusLabel: "CLOSED NOW",
    headerStatus: "CLOSED NOW",
    headerDetail,
    statusColor: "red",
    currentUKTime: formattedTime,
    weekday,
    nextOpening,
  };
}

/**
 * Custom hook to subscribe to live UK restaurant status, updated every minute
 */
export function useOpeningHours() {
  const [status, setStatus] = useState(() => getUKRestaurantStatus());

  useEffect(() => {
    const update = () => setStatus(getUKRestaurantStatus());
    update();
    const interval = setInterval(update, 30000); // refresh every 30s
    return () => clearInterval(interval);
  }, []);

  return status;
}
